import type { InmateDeviceFilters } from '@reliance/types/filters/InmateDeviceFilters';
import { InmateDevice } from '@reliance/types/models/InmateDevice';
import { useAsync } from 'react-async-hook';
import api from '../common/api';
import { PaginatedResponse } from '@reliance/types/responses/PaginatedResponse';

export const useInmateDevicesByInmateId = (
  inmateId: number | null | undefined,
  filters?: InmateDeviceFilters
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<InmateDevice>>(
    (currentInmateId?: number | null | undefined) => {
      const id = currentInmateId !== undefined ? currentInmateId : inmateId;
      if (!id) {
        return null;
      }

      const params: InmateDeviceFilters = {
        inmateId: id,
        ...filters,
      };

      return api
        .get<PaginatedResponse<InmateDevice>>('/inmate-device', { params })
        .then((r) => r.data);
    },
    inmateId === null ? [] : [inmateId, filterKey]
  );
};

export const useInmateDevicesBySmsVoiceNumberId = (
  smsVoiceNumberId: number | null | undefined,
  filters?: InmateDeviceFilters
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<InmateDevice>>(
    (currentSmsVoiceNumberId?: number | null | undefined) => {
      const id = currentSmsVoiceNumberId !== undefined ? currentSmsVoiceNumberId : smsVoiceNumberId;
      if (id == null || !Number.isFinite(id)) {
        return null;
      }

      const params: InmateDeviceFilters = {
        includeUnassigned: true,
        ...filters,
        smsVoiceNumberId: id,
      };

      return api
        .get<PaginatedResponse<InmateDevice>>('/inmate-device', { params })
        .then((r) => r.data);
    },
    [smsVoiceNumberId, filterKey]
  );
};

export const useInmateDevicesBySmsVoiceNumberPhoneNumber = (
  phoneNumber: string | null | undefined,
  filters?: Omit<InmateDeviceFilters, 'phoneNumber' | 'smsVoiceNumberId'>
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<InmateDevice>>(
    (
      currentPhoneNumber?: string | null | undefined,
      currentFilters?: InmateDeviceFilters
    ) => {
      const resolvedPhoneNumber =
        currentPhoneNumber !== undefined ? currentPhoneNumber : phoneNumber;
      if (resolvedPhoneNumber == null || resolvedPhoneNumber.trim() === '') {
        return null;
      }
      const baseFilters = currentFilters !== undefined ? currentFilters : filters;
      const params = {
        includeUnassigned: true,
        ...baseFilters,
        phoneNumber: resolvedPhoneNumber,
      } as InmateDeviceFilters;

      return api
        .get<PaginatedResponse<InmateDevice>>('/inmate-device', { params })
        .then((r) => r.data);
    },
    [phoneNumber, filterKey]
  );
};
